import State from 'store/State';
import { useSelector } from 'react-redux';
import { ActiveInputType } from '../PortfolioState';
import { WidgetInfo } from 'widgets/WidgetInfo';
import {
    isEmptyPortfolioSelection,
    PortfolioSelection,
} from 'portfolio/PortfolioSelection';
import { createSelector } from 'reselect';
import Order from 'communication/types/order/Order';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { selectOrdersStreamingOrderIds } from 'app/store/orders/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';

export const selectPortfolioState = (state: State) => state.portfolio;

export interface ActiveInput {
    activeInputType: ActiveInputType;
    activeInputWidget?: WidgetInfo;
}

export const selectActiveInput: (state: State) => ActiveInput = (state) => {
    const { activeInputType, activeInputWidget } = selectPortfolioState(state);
    return {
        activeInputType,
        activeInputWidget,
    };
};

const selectActiveInputSelector = createSelector(
    selectPortfolioState,
    (ps) => ({
        activeInputType: ps.activeInputType,
        activeInputWidget: ps.activeInputWidget,
    })
);

export const useSelectActiveInput = () => {
    return useSelector(selectActiveInputSelector);
};

export const selectPortfolioSelection: (
    state: State
) => PortfolioSelection | undefined = (state) => {
    return selectPortfolioState(state).selection;
};

export const useSelectPortfolioSelection = () => {
    return useSelector(selectPortfolioSelection);
};

export const selectPortfolioSelectedOrders = (state: State) =>
    selectPortfolioSelection(state)?.selectedOrders;

/*
 * Selects the orders streamed from Newport, or from the Portfolio Selection (i.e. selected in the Blotter in Standalone)
 * Returns undefined if the streamed or selected orders do not exist or are empty
 */
export function selectStreamedOrSelectedOrders(
    streamingOrders: Order[],
    selectionOrders: Order[] | undefined,
    activeInputUsesOrdersStream: boolean
) {
    /*
     * If we have streaming orders (i.e. we are in Newport), use them...
     */
    if (activeInputUsesOrdersStream && streamingOrders.length > 0) {
        return streamingOrders;
    }
    if (!selectionOrders || selectionOrders.length === 0) {
        return undefined;
    }
    /*
     * Otherwise use the selection (i.e. selected blotter rows in Standalone)
     */
    return selectionOrders;
}

export const selectPortfolioStreamingOrderIds = (state: State) => {
    let streamingOrderIds = selectOrdersStreamingOrderIds(state);
    const selection = selectPortfolioSelection(state);
    const selectionWrapper = new PortfolioSelectionWrapper(selection);
    if (streamingOrderIds.length === 0) {
        // No streaming order ids so extract from the selected orders if there are any..
        streamingOrderIds = selectionWrapper.selectedOrders.map(
            (o) => o.orderID
        );
    }
    return streamingOrderIds;
};

export const selectCurrentSymbology = (state: State) => {
    const portfolioSelection = selectPortfolioSelection(state);
    const appSettings = selectAppSettings(state);
    return portfolioSelection && !isEmptyPortfolioSelection(portfolioSelection)
        ? portfolioSelection.symbology
        : appSettings.symbology;
};

export const useSelectCurrentSymbology = () =>
    useSelector(selectCurrentSymbology);
